import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import styled from 'styled-components'

import { ToggleButton } from '@junglescout/edna'

import { useSelectedAmazonSellerAccount } from 'hooks/amazon_seller_account'
import { useNotification } from 'hooks/notification'
import { sendSegmentTrackEvent } from 'services/segment'
import { productType } from 'types/product_costs_and_settings'

import { ReviewAutomationModals } from 'src/js/components/review_automation/Modals/ReviewAutomationModals'
import { setGlobalModal } from 'actions/index'
import { TEXT_STYLES } from 'constants/text_styles'
import { openEditReviewAutomationSettingsModal } from './review_automation_acfions'
import { StyledTextCTA } from './Slideout/StyledTextCTA'
import { updateProductCostsAndSettingsCache } from './utils'

const EDIT_MARGIN_LEFT = '8px'

const StyledText = styled.div`
  padding-left: ${EDIT_MARGIN_LEFT};
`
const ButtonWrapper = styled.span`
  height: 20px;
`
const HighlightLightRed = styled.div`
  padding: 0 4px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.red100};
`
const HighlightYellow = styled.div`
  padding: 0 4px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.yellow100};
`
const StyledReviewAutomationModals = styled.div`
  .modal-backdrop {
    background-color: transparent;
  }
  .modal-container {
    box-shadow: none;
  }
`
const Link = styled.span`
  ${TEXT_STYLES.H4Orange}
  cursor: pointer;
  word-break: break-word;
  padding: 5px 0;
  margin-left: 8px;
`

export const ReviewAutomationCell = ({ product }) => {
  const { t } = useTranslation(['product_costs_and_settings', 'generic'])

  const {
    amazon_seller_account_id,
    selectedCountry
  } = useSelectedAmazonSellerAccount()

  const { review_automation_enabled, review_request_delay } =
    selectedCountry || {}

  const productAutomationStatus = useCallback(() => {
    if (review_automation_enabled) {
      return (
        product.review_request_enabled === true ||
        product.review_request_enabled === null
      )
    }
    return review_automation_enabled
  }, [product, review_automation_enabled])

  const [reviewRequestEnabled, setReviewRequestEnabled] = useState(() =>
    productAutomationStatus()
  )

  useEffect(() => {
    setReviewRequestEnabled(productAutomationStatus())
  }, [review_automation_enabled, productAutomationStatus])

  const dispatch = useDispatch()

  const queryClient = useQueryClient()
  const productId = product.product_id || product.id

  const createNotification = useNotification()

  const defaultDays = review_request_delay || 0

  const [reviewRequestDelay, setReviewRequestDelay] = useState(
    product.review_request_delay || defaultDays
  )

  const [prevIsEnabled, setPreviousEnabled] = useState(() => {
    return productAutomationStatus()
  })

  const [prevReviewRequestDelay, setPreviousRequestDelay] = useState(
    product.review_request_delay
  )

  const successNotificationEnabled = t(
    'product_costs_and_settings:reviewrequestupdatesuccessenabled',
    'Review Request has been scheduled to be sent {{ days }} days after order delivery',
    {
      days: reviewRequestDelay
    }
  )

  const successNotificationDisabled = t(
    'product_costs_and_settings:reviewrequestupdatesuccessdisabled',
    'Request to review the order will not be sent out after order delivery'
  )

  const { mutate: toggleReviewRequest } = useMutation(
    'api/junglekit/product/review_request_settings',
    {
      onSuccess: async response => {
        sendSegmentTrackEvent('CTA Clicked', {
          text: 'Review Automation Toggle',
          location: 'Product table',
          section: 'Product table',
          ASIN: product.asin,
          setToStatus: product.review_request_enabled
        })

        createNotification({
          title: 'Success!',
          message: response?.data?.review_request_enabled
            ? successNotificationEnabled
            : successNotificationDisabled,
          level: 'success'
        })

        updateProductCostsAndSettingsCache(
          [productId],
          queryClient,
          response?.data
        )
      },
      onError: async error => {
        await reverseOnError()
        createNotification({
          title: 'Error',
          message: error?.error,
          level: 'error'
        })
      }
    }
  )

  const updateReviewAutomation = async () => {
    await setReviewRequestDelay(product.review_request_delay)
    await setReviewRequestEnabled(product.review_request_enabled)
    const params = {
      review_request_enabled: product.review_request_enabled,
      amazon_seller_account_id,
      product_id: product.product_id
    }

    if (product.review_request_delay !== defaultDays) {
      params.review_request_delay = product.review_request_delay
    }

    toggleReviewRequest({
      data: params,
      method: 'PATCH'
    })
  }

  const toggleReviewRequestBtn = async e => {
    e.stopPropagation()
    await savePreviousValue()
    /* eslint no-param-reassign: "error" */
    product.review_request_enabled = !productAutomationStatus()
    if (product.review_request_enabled && !product.review_request_delay) {
      product.review_request_delay = defaultDays
    }
    updateReviewAutomation()
  }

  const reverseOnError = async () => {
    product.enabled = prevIsEnabled
    product.review_request_delay = prevReviewRequestDelay
    await setReviewRequestDelay(prevReviewRequestDelay)
    await setReviewRequestEnabled(prevIsEnabled)
  }

  const savePreviousValue = async () => {
    await setPreviousEnabled(product.review_request_enabled)
    await setPreviousRequestDelay(product.review_request_delay)
  }

  const saveProductChange = async changes => {
    await savePreviousValue()
    if (Object.keys(changes).length === 0) {
      return
    }
    if (changes.review_request_delay) {
      product.review_request_enabled = true
      product.review_request_delay = changes.review_request_delay
    } else if (!changes.review_request_enabled) {
      product.review_request_enabled = changes.review_request_enabled
    } else if (
      changes.review_request_enabled &&
      !changes.review_request_delay
    ) {
      product.review_request_enabled = true
      product.review_request_delay = product.review_request_delay || defaultDays
    }

    updateReviewAutomation()
  }

  const editReviewRequest = e => {
    e.stopPropagation()
    const reviewRequstModelProduct = {
      id: productId,
      title: product.title,
      review_request_enabled: reviewRequestEnabled,
      review_request_delay: reviewRequestDelay,
      updateProductReviewRequestSettings: saveProductChange
    }
    dispatch(openEditReviewAutomationSettingsModal(reviewRequstModelProduct))
  }

  const doNotRequest = (
    <HighlightLightRed>
      {t('product_costs_and_settings:donotrequest', 'Do not request')}
    </HighlightLightRed>
  )

  const scheduled = (
    <HighlightYellow>
      {t('product_costs_and_settings:scheduled', 'Scheduled {{ days }} Days', {
        days: reviewRequestDelay || defaultDays
      })}
    </HighlightYellow>
  )

  const renderStatusAction = () => {
    if (review_automation_enabled) {
      return (
        <>
          <ButtonWrapper onClick={toggleReviewRequestBtn}>
            <ToggleButton checked={reviewRequestEnabled} />
          </ButtonWrapper>
          <StyledText>
            {reviewRequestEnabled ? scheduled : doNotRequest}
          </StyledText>
          <StyledTextCTA
            text={t('generic:edit', 'Edit')}
            title={t('product_costs_and_settings:editButton', 'Edit Button')}
            onClick={editReviewRequest}
            marginLeft={EDIT_MARGIN_LEFT}
          />
        </>
      )
    }
    return (
      <>
        <HighlightLightRed>
          {t(
            'product_costs_and_settings:review_automation_off',
            'Review Automation: OFF'
          )}
        </HighlightLightRed>
        <Link
          onClick={e => {
            e.stopPropagation()
            dispatch(setGlobalModal('EnableReviewAutomationModal'))
          }}>
          {t('product_costs_and_settings:review_automation_enable', 'Enable')}
        </Link>
      </>
    )
  }

  return (
    <>
      {renderStatusAction()}
      <span
        onClick={e => {
          e.stopPropagation()
        }}>
        <StyledReviewAutomationModals>
          <ReviewAutomationModals />
        </StyledReviewAutomationModals>
      </span>
    </>
  )
}

ReviewAutomationCell.propTypes = {
  product: productType.isRequired
}
